import { useState } from 'react';
import dynamic from 'next/dynamic';
import ImageSource from '@components/ImageSource/ImageSource';
import { IParsedImage, IVideoType } from '@components/typescript';
import { TFullImageWithHeading } from '@features/cms/typescript';
import styles from '@features/cms/components/pages/FullImageWithHeading/FullImageWithHeading.styles';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import Button from '@components/Button/Button';
import sanitizeHtml from '@utils/sanitizeHtml';
import scrollToElementById from '@utils/scrollToElementById';
type PropsType = {
  data: TFullImageWithHeading;
  isFirst?: boolean;
};
const DynamicVideoModal = dynamic(() => import('@components/VideoModal/VideoModal'), {
  ssr: false
});
const FullImageWithHeading = ({
  data,
  isFirst = false
}: PropsType) => {
  const CONFIG_SANITIZE = {
    allowedTags: ['br']
  };
  const [isOpenModal, setIsModalOpen] = useState(false);
  const {
    imagesWithBlur,
    backgroundImage,
    title,
    description,
    buttonText,
    buttonUrl,
    video,
    thumbnail,
    code
  } = data;
  let imageSources: IParsedImage | null = backgroundImage ? {
    image: backgroundImage
  } : null;
  const shouldShowVideo = !!video && !!buttonText;
  const subtitle = data?.subtitle ? data.subtitle : null;
  const variant = data?.variant ? data.variant : 'default';
  const variantContent = data?.variantContent ? data.variantContent : 'center';
  const variantAlignContent = data?.variantAlignContent ? data.variantAlignContent : 'center';
  const contentWidth = data?.contentWidth ? data.contentWidth : 'medium';
  if (imagesWithBlur) {
    imageSources = {
      image: imagesWithBlur.image,
      blur: imagesWithBlur.blur
    };
  }
  const renderTitle = (): JSX.Element => {
    if (variant === 'other') {
      return <Heading h={isFirst ? 1 : 3} display={3} color={theme.rawColors.neutralWhite} pb={16} mobile>
          {title}
        </Heading>;
    }
    return <Heading h={isFirst ? 1 : 2} display={2} color={theme.rawColors.neutralWhite} pb={16} mobile>
        {title}
      </Heading>;
  };
  const openUrl = (url: string) => {
    window.location.href = url;
  };
  const renderSubtitle = (): JSX.Element | null => {
    if (variant === 'other' && subtitle !== '' && subtitle !== null) {
      return <Heading h={4} headingType={4} color={theme.rawColors.neutralWhite} pb={40} mobile>
          {subtitle}
        </Heading>;
    }
    return null;
  };
  const renderButton = (): JSX.Element | null => buttonUrl && buttonUrl.slice(0, 1) === '#' ? <Button variant="outlined" variantColor="white" size="small" letterCase="lowercase" onClick={() => scrollToElementById(buttonUrl.substring(1))}>
        {buttonText}
      </Button> : <Button variant="outlined" variantColor="white" size="small" letterCase="lowercase" onClick={() => buttonUrl ? openUrl(buttonUrl) : setIsModalOpen(true)}>
        {buttonText}
      </Button>;
  return <section id={code} sx={styles.fullImageWithHeading(variant, variantContent, variantAlignContent, contentWidth)}>
      {imageSources && <ImageSource imageSources={imageSources} sizes="100vw" alt={title} priority={isFirst} />}
      <div sx={styles.fullImageContent(variant, variantContent, contentWidth)}>
        {renderTitle()}
        {renderSubtitle()}
        <p sx={styles.textDescription} color={theme.rawColors.neutralWhite}>
          <span dangerouslySetInnerHTML={sanitizeHtml(description, CONFIG_SANITIZE)} />
        </p>
        {buttonText && renderButton()}
        {shouldShowVideo && <>
            {/* @ts-expect-error - React 19 compatibility issue with next/dynamic types */}
            <DynamicVideoModal active={isOpenModal} closeModalFunc={setIsModalOpen} video={video as IVideoType} thumbnail={thumbnail} />
          </>}
      </div>
    </section>;
};
export default FullImageWithHeading;